// 261015 P4c-1 (D8/Q3) — FORM SỬA TRỰC TIẾP "Tổng quan truyện": đặc tả trường + kiểm hợp lệ + dựng lệnh PATCH. THUẦN (jest). Không import antd/react.
// Một lần lưu = một PATCH nhiều lệnh, gửi kèm `expectedModifiedAt`. Server cấp mọi id/neo/trạng thái — FE KHÔNG gửi `status`, offset, anchor; chỉ gửi chữ trích để server TỰ định vị lại.
// Mọi luật ở đây phản chiếu `admin-service/src/utils/overviewPatch.ts` (BE vẫn là chốt chặn cuối): kiểm sớm để người dùng không phải đợi 400.
import type {
  FilmOutline, MotiveGroup, Overview, OutlinePatchOp, OverviewClaimKind, OverviewDisposition, OverviewPlotKey, OverviewPlotStatus, OverviewRelationshipType, OverviewSection,
} from '../../../../types/adaptation';
import { RELATION_LABEL } from './relationGraphLayout';
import { CLAIM_KIND_LABEL, claimUsage, DISPOSITION_TEXT, GOLDEN_FINGER_HONESTY, GOLDEN_FINGER_MAX, MOTIVE_GROUPS, MOTIVE_NOTES_MAX, MOTIVE_NOTE_HONESTY, PLOT_LABEL, PLOT_STATUS_TEXT } from './overviewView';

export const LIMITS = { claimText: 1200, quotes: 4, quoteMin: 12, quoteMax: 1600, introWords: 30, motiveNoteText: 1200, motiveNotesPerGroup: MOTIVE_NOTES_MAX, goldenFingerClaims: GOLDEN_FINGER_MAX } as const;

export type EditKind = 'claim' | 'claim-add' | 'plot' | 'strategy' | 'introduction' | 'profile' | 'prop' | 'rule' | 'faction' | 'relationship' | 'introLine' | 'golden-finger' | 'motive-note';
export type FieldType = 'text' | 'textarea' | 'select' | 'multi' | 'quotes' | 'tags';
export type FieldSource = 'claims' | 'profiles' | 'cast' | 'factions' | 'rules' | 'introLines' | 'static' | 'subjects';
export interface Option { value: string; label: string }
export interface FieldSpec { key: string; label: string; type: FieldType; source?: FieldSource; options?: Option[]; required?: boolean; help?: string; rows?: number; allowEmpty?: boolean }
export type Values = Record<string, any>;

/** Đích của form: sửa một thực thể có sẵn (`id`) hoặc thêm mới (`id` rỗng). */
export interface EditTarget { id?: string; section?: OverviewSection; ownerId?: string; part?: OverviewPlotKey; profileId?: string; /** `motive-note`: nhóm của ghi chú (cố định — sửa không đổi được nhóm). */ group?: MotiveGroup }

export interface FormSpec {
  kind: EditKind; title: string; okText: string; fields: FieldSpec[]; initial: Values;
  /** Có ⇒ nút "Xoá" (kèm hộp hỏi) với lệnh xoá + lời cảnh báo cascade. */
  remove?: { label: string; confirm: string; ops: OutlinePatchOp[] };
}

const REL_OPTIONS: Option[] = (Object.keys(RELATION_LABEL) as OverviewRelationshipType[]).map((k) => ({ value: k, label: RELATION_LABEL[k] }));
const DISP_OPTIONS: Option[] = (['KEPT', 'MERGED', 'CUT', 'UNMAPPED'] as OverviewDisposition[]).map((k) => ({ value: k, label: DISPOSITION_TEXT[k] }));
const PLOT_STATUS_OPTIONS: Option[] = (['KNOWN', 'NOT_REACHED', 'UNKNOWN'] as OverviewPlotStatus[]).map((k) => ({ value: k, label: PLOT_STATUS_TEXT[k] }));
const PURPOSE_OPTIONS: Option[] = [{ value: 'CHARACTER', label: 'Giới thiệu một nhân vật' }, { value: 'WORLD_RULE', label: 'Giới thiệu một luật thế giới' }];
const FACTION_KIND_OPTIONS: Option[] = [{ value: 'family', label: 'Gia tộc / huyết thống' }, { value: 'organization', label: 'Tổ chức / thế lực' }];

const words = (t: string): number => t.trim().split(/\s+/).filter(Boolean).length;
const strs = (v: unknown): string[] => (Array.isArray(v) ? v.map((x) => String(x ?? '').trim()).filter(Boolean) : []);
const s = (v: unknown): string => String(v ?? '').trim();

/** Chữ trích hiện có của một ý (neo đã định vị + trích AI/người đưa mà code không định vị được) — để sửa lại. */
export const existingQuotes = (ov: Overview, claimId: string): string[] => {
  const c = ov.claims.find((x) => x.id === claimId);
  if (!c) return [];
  const anchors = new Map(ov.anchors.map((a) => [a.id, a] as const));
  return [...c.anchorIds.map((id) => anchors.get(id)?.quote ?? '').filter(Boolean), ...(c.unlocatedQuotes ?? [])];
};

/** Cảnh báo mềm về trích (BE KHÔNG từ chối — ý vẫn lưu nhưng `chưa xác minh` và chặn chốt). */
export const quoteWarnings = (quotes: string[]): string[] => {
  const out: string[] = [];
  const q = strs(quotes);
  if (!q.length) out.push('Chưa có trích nào — ý sẽ ở trạng thái "chưa xác minh" và chặn chốt cho tới khi có trích.');
  q.forEach((t, i) => {
    if (t.length < LIMITS.quoteMin) out.push(`Trích ${i + 1} quá ngắn (${t.length} ký tự, tối thiểu ${LIMITS.quoteMin}) — sẽ không định vị được.`);
    if (t.length > LIMITS.quoteMax) out.push(`Trích ${i + 1} quá dài (${t.length} ký tự, tối đa ${LIMITS.quoteMax}) — sẽ không định vị được.`);
  });
  return out;
};

// ─── Đặc tả form ─────────────────────────────────────────────────────────────────────────────────────────

export const SECTION_LABEL: Record<OverviewSection, string> = {
  synopsis: 'Tóm tắt truyện', 'plot.beginning': 'Đoạn Mở đầu', 'plot.continuation': 'Đoạn Tiếp diễn', 'plot.turn': 'Đoạn Bước ngoặt', 'plot.ending': 'Đoạn Kết thúc',
  'profile.personality': 'Hồ sơ · tính cách', 'profile.behavior': 'Hồ sơ · bằng chứng hành vi', 'profile.setup': 'Hồ sơ · xuất thân', 'profile.roleLabel': 'Hồ sơ · vai', 'profile.roleTitle': 'Hồ sơ · chức danh',
  'profile.appearance.current': 'Hồ sơ · ngoại hình hiện tại', 'profile.appearance.before': 'Hồ sơ · ngoại hình trước', 'profile.appearance.after': 'Hồ sơ · ngoại hình sau',
  introduction: 'Giới thiệu tập 1', prop: 'Đạo cụ', worldRule: 'Luật thế giới', relationship: 'Quan hệ', faction: 'Phe', introLine: 'Câu giới thiệu', goldenFinger: 'Động cơ · bàn tay vàng',
};

/** Loại ý chọn được khi thêm ý MỚI vào bàn tay vàng (Q6) — "học võ từ nhỏ" là CHARACTER nên mặc định `CHARACTER`. */
const CLAIM_KIND_OPTIONS: Option[] = (['CHARACTER', 'PLOT', 'RELATION', 'PROP', 'RULE'] as OverviewClaimKind[]).map((k) => ({ value: k, label: CLAIM_KIND_LABEL[k] ?? k }));
const CLAIM_KINDS: ReadonlyArray<string> = CLAIM_KIND_OPTIONS.map((o) => o.value);
const motiveGroup = (g: MotiveGroup | undefined) => MOTIVE_GROUPS.find((x) => x.group === g);

const claimFields: FieldSpec[] = [
  { key: 'text', label: 'Nội dung ý (tiếng Việt, một ý)', type: 'textarea', rows: 3, required: true, help: `Tối đa ${LIMITS.claimText} ký tự. Chỉ kể đúng điều trích nói — không thêm chi tiết, không chắc chắn hơn trích.` },
  { key: 'quotes', label: 'Trích nguyên văn từ nguồn (tối đa 4)', type: 'quotes', help: 'Chép đúng từng ký tự, liên tục, đủ dài để chỉ khớp MỘT chỗ. Lưu sẽ tự đối chiếu với nguyên tác — câu viết vẫn cần người đọc kiểm đúng nghĩa.' },
];

export const formSpec = (kind: EditKind, t: EditTarget, ov: Overview, outline?: FilmOutline | null): FormSpec => {
  switch (kind) {
    case 'claim': {
      const c = ov.claims.find((x) => x.id === t.id);
      const use = claimUsage(ov, t.id ?? '');
      return {
        kind, title: `Sửa ý ${t.id}`, okText: 'Lưu ý', fields: claimFields, initial: { text: c?.text ?? '', quotes: existingQuotes(ov, t.id ?? '') },
        remove: { label: 'Xoá ý', confirm: use.length ? `Ý ${t.id} đang được dùng ở: ${use.slice(0, 6).join('; ')}${use.length > 6 ? '…' : ''}. Xoá sẽ gỡ khỏi các chỗ đó; chỗ nào chỉ còn ý này làm nền thì BE từ chối.` : `Xoá ý ${t.id}?`, ops: [{ op: 'removeOverviewClaim', id: t.id ?? '' }] },
      };
    }
    case 'claim-add': {
      const gold = t.section === 'goldenFinger';
      return {
        kind, title: `Thêm ý vào "${SECTION_LABEL[t.section as OverviewSection] ?? ''}"`, okText: 'Thêm ý',
        fields: gold ? [{ key: 'kind', label: 'Loại ý', type: 'select', options: CLAIM_KIND_OPTIONS, required: true, help: 'Chọn loại gần nhất với điều ý nói (vd "học võ từ nhỏ" là Nhân vật). Ý mới vào bàn tay vàng và cần có trích như ý thường.' }, ...claimFields] : claimFields,
        initial: gold ? { kind: 'CHARACTER', text: '', quotes: [''] } : { text: '', quotes: [''] },
      };
    }
    case 'plot': {
      const p = ov.plot[t.part as OverviewPlotKey];
      return {
        kind, title: `Sửa đoạn ${PLOT_LABEL[t.part as OverviewPlotKey]}`, okText: 'Lưu đoạn',
        fields: [
          { key: 'status', label: 'Trạng thái đoạn', type: 'select', options: PLOT_STATUS_OPTIONS, required: true, help: '"Đã có trong nguồn" cần ít nhất một ý nền. Đoạn nguồn chưa tới / chưa đủ ý thì không gắn ý.' },
          { key: 'claimIds', label: 'Ý nền', type: 'multi', source: 'claims' },
          { key: 'note', label: 'Ghi chú (đề xuất chuyển thể, ngắn)', type: 'textarea', rows: 2 },
        ],
        initial: { status: p?.status ?? 'UNKNOWN', claimIds: p?.claimIds ?? [], note: p?.note ?? '' },
      };
    }
    case 'strategy':
      return { kind, title: 'Sửa chiến lược mở đầu tập 1', okText: 'Lưu', fields: [{ key: 'approach', label: 'Chiến lược (đề xuất chuyển thể — không phải sự việc nguồn)', type: 'textarea', rows: 4 }], initial: { approach: ov.firstEpisodeStrategy.approach } };
    case 'introduction': {
      const i = ov.firstEpisodeStrategy.introductions.find((x) => x.profileId === t.profileId);
      return {
        kind, title: i ? 'Sửa giới thiệu tập 1' : 'Thêm giới thiệu tập 1', okText: 'Lưu giới thiệu',
        fields: [
          { key: 'profileId', label: 'Giới thiệu ai', type: 'select', source: 'profiles', required: true },
          { key: 'how', label: 'Cách giới thiệu (đề xuất chuyển thể)', type: 'textarea', rows: 3 },
          { key: 'basisClaimIds', label: 'Ý nền (ít nhất một)', type: 'multi', source: 'claims', required: true },
          { key: 'introLineId', label: 'Câu V.O. giới thiệu đi kèm', type: 'select', source: 'introLines', allowEmpty: true },
        ],
        initial: { profileId: t.profileId ?? '', how: i?.how ?? '', basisClaimIds: i?.basisClaimIds ?? [], introLineId: i?.introLineId ?? '' },
        remove: i ? { label: 'Bỏ giới thiệu', confirm: 'Bỏ mục giới thiệu tập 1 của nhân vật này?', ops: [{ op: 'removeOverviewIntroduction', profileId: i.profileId }] } : undefined,
      };
    }
    case 'profile': {
      const p = ov.profiles.find((x) => x.id === t.id);
      return {
        kind, title: p ? `Sửa hồ sơ ${p.sourceName}` : 'Thêm hồ sơ nhân vật', okText: 'Lưu hồ sơ',
        fields: [
          { key: 'sourceName', label: 'Tên trong nguyên tác', type: 'text', required: true },
          { key: 'disposition', label: 'Số phận trong phim', type: 'select', options: DISP_OPTIONS, required: true, help: 'Đã cắt / chưa ánh xạ thì không trỏ nhân vật nào. Giữ / đã gộp phải trỏ một nhân vật trong dàn nhân vật.' },
          { key: 'characterId', label: 'Nhân vật trong dàn nhân vật', type: 'select', source: 'cast', allowEmpty: true },
          { key: 'roleLabel', label: 'Vai', type: 'multi', source: 'claims' },
          { key: 'roleTitle', label: 'Chức danh / thân phận', type: 'multi', source: 'claims' },
          { key: 'setup', label: 'Xuất thân / thiết lập', type: 'multi', source: 'claims' },
          { key: 'personality', label: 'Tính cách', type: 'multi', source: 'claims', help: 'Câu nguyên tác TẢ THẲNG tính cách / khí chất.' },
          { key: 'behavior', label: 'Bằng chứng hành vi', type: 'multi', source: 'claims', help: 'Ý KỂ MỘT HÀNH ĐỘNG cho thấy cách nhân vật phản ứng (đối đầu, bảo vệ, mưu mẹo…). Tối đa 5 ý là mức gợi ý của bước gộp.' },
          { key: 'appearanceCurrent', label: 'Ngoại hình hiện tại', type: 'multi', source: 'claims' },
          { key: 'appearanceBefore', label: 'Ngoại hình trước khi đổi', type: 'multi', source: 'claims' },
          { key: 'appearanceAfter', label: 'Ngoại hình sau khi đổi', type: 'multi', source: 'claims' },
          { key: 'appearanceNote', label: 'Ghi chú ngoại hình', type: 'textarea', rows: 2 },
        ],
        initial: {
          sourceName: p?.sourceName ?? '', disposition: p?.disposition ?? 'UNMAPPED', characterId: p?.characterId ?? '',
          roleLabel: p?.roleLabel ?? [], roleTitle: p?.roleTitle ?? [], setup: p?.setup ?? [], personality: p?.personality ?? [], behavior: p?.behavior ?? [],
          appearanceCurrent: p?.appearance.current ?? [], appearanceBefore: p?.appearance.before ?? [], appearanceAfter: p?.appearance.after ?? [], appearanceNote: p?.appearance.note ?? '',
        },
        remove: p ? { label: 'Xoá hồ sơ', confirm: `Xoá hồ sơ ${p.sourceName}? Kéo theo xoá: giới thiệu tập 1, các quan hệ, câu V.O. của nhân vật này; phe còn dưới 2 người bị bỏ.`, ops: [{ op: 'removeOverviewProfile', id: p.id }] } : undefined,
      };
    }
    case 'prop': {
      const p = ov.keyProps.find((x) => x.id === t.id);
      return {
        kind, title: p ? `Sửa đạo cụ ${p.name}` : 'Thêm đạo cụ', okText: 'Lưu đạo cụ',
        fields: [
          { key: 'name', label: 'Tên đạo cụ (như nguyên tác)', type: 'text', required: true },
          { key: 'aliases', label: 'Tên gọi khác', type: 'tags' },
          { key: 'description', label: 'Ý mô tả (ít nhất một)', type: 'multi', source: 'claims', required: true },
          { key: 'importance', label: 'Vì sao quan trọng với phim (đề xuất chuyển thể)', type: 'textarea', rows: 2 },
        ],
        initial: { name: p?.name ?? '', aliases: p?.aliases ?? [], description: p?.description ?? [], importance: p?.importance ?? '' },
        remove: p ? { label: 'Xoá đạo cụ', confirm: `Xoá đạo cụ ${p.name}?`, ops: [{ op: 'removeOverviewProp', id: p.id }] } : undefined,
      };
    }
    case 'rule': {
      const w = ov.worldRules.find((x) => x.id === t.id);
      return {
        kind, title: w ? `Sửa luật ${w.title}` : 'Thêm luật thế giới', okText: 'Lưu luật',
        fields: [{ key: 'title', label: 'Luật (một câu ngắn)', type: 'text', required: true }, { key: 'claimIds', label: 'Ý nền (ít nhất một)', type: 'multi', source: 'claims', required: true }],
        initial: { title: w?.title ?? '', claimIds: w?.claimIds ?? [] },
        remove: w ? { label: 'Xoá luật', confirm: `Xoá luật "${w.title}"? Kéo theo xoá câu V.O. giới thiệu luật này.`, ops: [{ op: 'removeOverviewWorldRule', id: w.id }] } : undefined,
      };
    }
    case 'faction': {
      const f = ov.factions.find((x) => x.id === t.id);
      return {
        kind, title: f ? `Sửa phe ${f.label}` : 'Thêm phe / gia tộc', okText: 'Lưu phe',
        fields: [
          { key: 'label', label: 'Tên phe', type: 'text', required: true },
          { key: 'kind', label: 'Loại', type: 'select', options: FACTION_KIND_OPTIONS, required: true },
          { key: 'memberProfileIds', label: 'Thành viên (ít nhất 2)', type: 'multi', source: 'profiles', required: true },
          { key: 'claimIds', label: 'Ý nền (ít nhất một)', type: 'multi', source: 'claims', required: true },
        ],
        initial: { label: f?.label ?? '', kind: f?.kind ?? 'family', memberProfileIds: f?.memberProfileIds ?? [], claimIds: f?.claimIds ?? [] },
        remove: f ? { label: 'Xoá phe', confirm: `Xoá phe "${f.label}"? Các quan hệ thuộc phe này chỉ mất nhãn phe.`, ops: [{ op: 'removeOverviewFaction', id: f.id }] } : undefined,
      };
    }
    case 'relationship': {
      const r = ov.relationships.find((x) => x.id === t.id);
      return {
        kind, title: r ? `Sửa quan hệ ${r.id}` : 'Thêm quan hệ', okText: 'Lưu quan hệ',
        fields: [
          { key: 'aProfileId', label: 'Người A (với "cha/mẹ → con", "chủ → tớ": A là cha mẹ / chủ)', type: 'select', source: 'profiles', required: true },
          { key: 'bProfileId', label: 'Người B', type: 'select', source: 'profiles', required: true },
          { key: 'type', label: 'Loại quan hệ', type: 'select', options: REL_OPTIONS, required: true },
          { key: 'factionId', label: 'Thuộc phe', type: 'select', source: 'factions', allowEmpty: true },
          { key: 'claimIds', label: 'Ý nền nói đúng quan hệ này (ít nhất một)', type: 'multi', source: 'claims', required: true },
        ],
        initial: { aProfileId: r?.aProfileId ?? '', bProfileId: r?.bProfileId ?? '', type: r?.type ?? 'ally', factionId: r?.factionId ?? '', claimIds: r?.claimIds ?? [] },
        remove: r ? { label: 'Xoá quan hệ', confirm: `Xoá quan hệ ${r.id}?`, ops: [{ op: 'removeOverviewRelationship', id: r.id }] } : undefined,
      };
    }
    case 'introLine': {
      const l = ov.introLines.find((x) => x.id === t.id);
      return {
        kind, title: l ? `Sửa câu V.O. ${l.id}` : 'Thêm câu V.O. giới thiệu', okText: 'Lưu câu V.O.',
        fields: [
          { key: 'purpose', label: 'Mục đích', type: 'select', options: PURPOSE_OPTIONS, required: true },
          { key: 'subjectId', label: 'Nói về', type: 'select', source: 'subjects', required: true },
          { key: 'text', label: `Câu V.O. (ngôn ngữ phim, tối đa ${LIMITS.introWords} từ)`, type: 'textarea', rows: 2, required: true, help: 'Giọng người dẫn truyện; chỉ dùng điều có trong ý nền.' },
          { key: 'basisClaimIds', label: 'Ý nền (ít nhất một)', type: 'multi', source: 'claims', required: true },
        ],
        initial: { purpose: l?.purpose ?? 'CHARACTER', subjectId: l?.subjectId ?? '', text: l?.text ?? '', basisClaimIds: l?.basisClaimIds ?? [] },
        remove: l ? { label: 'Xoá câu V.O.', confirm: `Xoá câu V.O. ${l.id}?`, ops: [{ op: 'removeOverviewIntroLine', id: l.id }] } : undefined,
      };
    }
    case 'golden-finger':
      return {
        kind, title: 'Sửa bàn tay vàng', okText: 'Lưu bàn tay vàng',
        fields: [{ key: 'claimIds', label: `Ý là bàn tay vàng (tối đa ${GOLDEN_FINGER_MAX})`, type: 'multi', source: 'claims', allowEmpty: true, help: `Lợi thế đặc biệt của nhân vật chính, có trích nguồn (mọi loại ý đều được). ${GOLDEN_FINGER_HONESTY} Cần ý mới? Đóng hộp này rồi bấm "+ Thêm ý" ở mục Bàn tay vàng.` }],
        initial: { claimIds: ov.motives?.goldenFinger?.claimIds ?? [] },
      };
    case 'motive-note': {
      const g = motiveGroup(t.group);
      const n = t.id ? ov.motives?.[t.group as MotiveGroup]?.find((x) => x.id === t.id) : undefined;
      return {
        kind, title: n ? `Sửa ghi chú ${n.id} · ${g?.label ?? ''}` : `Thêm ghi chú · ${g?.label ?? ''}`, okText: 'Lưu ghi chú',
        fields: [
          { key: 'text', label: `Ghi chú biên kịch (đề xuất, tối đa ${LIMITS.motiveNoteText} ký tự)`, type: 'textarea', rows: 4, required: true, help: `${g?.help ?? ''} ${MOTIVE_NOTE_HONESTY} Không kể thêm sự việc mới.`.trim() },
          { key: 'basisClaimIds', label: 'Ý nền (ít nhất một)', type: 'multi', source: 'claims', required: true },
        ],
        initial: { text: n?.text ?? '', basisClaimIds: n?.basisClaimIds ?? [] },
        remove: n ? { label: 'Xoá ghi chú', confirm: `Xoá ghi chú ${n.id}?`, ops: [{ op: 'removeOverviewMotiveNote', id: n.id }] } : undefined,
      };
    }
    default:
      throw new Error(`kind không hỗ trợ: ${String(kind)}`);
  }
};

// ─── Kiểm hợp lệ (phản chiếu BE) ───────────────────────────────────────────────────────────────────────────

/** Danh sách lỗi chặn lưu (rỗng = được). Cảnh báo mềm của trích dùng `quoteWarnings`. */
export const validateForm = (kind: EditKind, v: Values, ov: Overview, t: EditTarget = {}): string[] => {
  const errs: string[] = [];
  const need = (cond: boolean, msg: string): void => { if (!cond) errs.push(msg); };
  switch (kind) {
    case 'claim': case 'claim-add': {
      const tx = s(v.text);
      need(!!tx, 'Ý không được để trống');
      need(tx.length <= LIMITS.claimText, `Ý dài ${tx.length} ký tự, vượt trần ${LIMITS.claimText}`);
      need(strs(v.quotes).length <= LIMITS.quotes, `Tối đa ${LIMITS.quotes} trích mỗi ý`);
      if (kind === 'claim-add' && t.section === 'goldenFinger') {
        need(CLAIM_KINDS.includes(s(v.kind)), 'Chọn loại ý');
        need((ov.motives?.goldenFinger?.claimIds?.length ?? 0) < LIMITS.goldenFingerClaims, `Bàn tay vàng đã đủ ${LIMITS.goldenFingerClaims} ý — bỏ bớt trước`);
      }
      break;
    }
    case 'golden-finger':
      need(strs(v.claimIds).length <= LIMITS.goldenFingerClaims, `Bàn tay vàng tối đa ${LIMITS.goldenFingerClaims} ý`);
      break;
    case 'motive-note': {
      const tx = s(v.text);
      need(!!motiveGroup(t.group), 'Thiếu nhóm ghi chú');
      need(!!tx, 'Ghi chú không được để trống');
      need(tx.length <= LIMITS.motiveNoteText, `Ghi chú dài ${tx.length} ký tự, vượt trần ${LIMITS.motiveNoteText}`);
      need(strs(v.basisClaimIds).length > 0, 'Ghi chú cần ít nhất một ý nền (đề xuất không có ý nền bị từ chối)');
      need(!!t.id || (ov.motives?.[t.group as MotiveGroup]?.length ?? 0) < LIMITS.motiveNotesPerGroup, `Nhóm này đã đủ ${LIMITS.motiveNotesPerGroup} ghi chú — sửa hoặc xoá bớt trước`);
      break;
    }
    case 'plot':
      need(['KNOWN', 'NOT_REACHED', 'UNKNOWN'].includes(s(v.status)), 'Chọn trạng thái đoạn');
      need(s(v.status) !== 'KNOWN' || strs(v.claimIds).length > 0, 'Đoạn "Đã có trong nguồn" cần ít nhất một ý nền');
      break;
    case 'strategy': break;
    case 'introduction':
      need(!!s(v.profileId), 'Chọn nhân vật cần giới thiệu');
      need(strs(v.basisClaimIds).length > 0, 'Giới thiệu tập 1 cần ít nhất một ý nền');
      break;
    case 'profile': {
      need(!!s(v.sourceName), 'Hồ sơ cần có tên nguyên tác');
      const sn = s(v.sourceName).toLocaleLowerCase('vi');
      need(!ov.profiles.some((p) => p.sourceName.toLocaleLowerCase('vi') === sn && p.id !== t.id), `Đã có hồ sơ trùng tên "${s(v.sourceName)}"`);
      const d = s(v.disposition);
      need(['KEPT', 'MERGED', 'CUT', 'UNMAPPED'].includes(d), 'Chọn số phận trong phim');
      need(!((d === 'KEPT' || d === 'MERGED') && !s(v.characterId)), 'Giữ / Đã gộp phải trỏ một nhân vật trong dàn nhân vật');
      break;
    }
    case 'prop':
      need(!!s(v.name), 'Đạo cụ cần có tên');
      need(strs(v.description).length > 0, 'Đạo cụ cần ít nhất một ý mô tả');
      break;
    case 'rule':
      need(!!s(v.title), 'Luật cần có tiêu đề');
      need(strs(v.claimIds).length > 0, 'Luật cần ít nhất một ý nền');
      break;
    case 'faction':
      need(!!s(v.label), 'Phe cần có tên');
      need(strs(v.memberProfileIds).length >= 2, 'Phe cần ít nhất 2 thành viên');
      need(strs(v.claimIds).length > 0, 'Phe cần ít nhất một ý nền');
      break;
    case 'relationship':
      need(!!s(v.aProfileId) && !!s(v.bProfileId), 'Chọn đủ hai người');
      need(s(v.aProfileId) !== s(v.bProfileId) || !s(v.aProfileId), 'Hai đầu quan hệ phải là hai người khác nhau');
      need(!!s(v.type), 'Chọn loại quan hệ');
      need(strs(v.claimIds).length > 0, 'Quan hệ cần ít nhất một ý nền');
      break;
    case 'introLine': {
      need(!!s(v.subjectId), 'Chọn người / luật mà câu V.O. nói tới');
      const t = s(v.text);
      need(!!t, 'Câu V.O. không được để trống');
      need(words(t) <= LIMITS.introWords, `Câu V.O. dài ${words(t)} từ, tối đa ${LIMITS.introWords}`);
      need(strs(v.basisClaimIds).length > 0, 'Câu V.O. cần ít nhất một ý nền');
      break;
    }
    default: break;
  }
  return errs;
};

// ─── Dựng lệnh PATCH ───────────────────────────────────────────────────────────────────────────────────────

/** Lệnh PATCH cho một lần lưu form. `id` rỗng ⇒ server cấp id. KHÔNG bao giờ gửi `status`/neo/offset. */
const sameList = (a: string[], b: string[]): boolean => a.length === b.length && a.every((x, i) => x === b[i]);

/**
 * `initial` = giá trị form lúc MỞ (`FormSpec.initial`): dùng để biết người có ĐỔI `behavior` không (Q5) — chỉ khi đổi mới gửi, vì `behavior: []` gửi vô cớ làm đổi dấu nội dung của hồ sơ cũ (chưa có khoá).
 */
export const buildOps = (kind: EditKind, v: Values, t: EditTarget, initial?: Values): OutlinePatchOp[] => {
  switch (kind) {
    case 'claim': return [{ op: 'setOverviewClaim', id: t.id ?? '', text: s(v.text), quotes: strs(v.quotes) }];
    case 'claim-add': return [{ op: 'addOverviewClaim', section: t.section as OverviewSection, ownerId: t.ownerId ?? '', text: s(v.text), quotes: strs(v.quotes), ...(t.section === 'goldenFinger' ? { kind: (s(v.kind) || 'CHARACTER') as OverviewClaimKind } : {}) }];
    case 'golden-finger': return [{ op: 'setOverviewGoldenFinger', claimIds: strs(v.claimIds) }];
    case 'motive-note': return [{ op: 'upsertOverviewMotiveNote', id: t.id ?? '', group: t.group as MotiveGroup, text: s(v.text), basisClaimIds: strs(v.basisClaimIds) }];
    case 'plot': return [{ op: 'setOverviewPlotPart', part: t.part as OverviewPlotKey, status: s(v.status) as OverviewPlotStatus, claimIds: s(v.status) === 'KNOWN' ? strs(v.claimIds) : [], note: s(v.note) }];
    case 'strategy': return [{ op: 'setOverviewStrategy', approach: s(v.approach) }];
    case 'introduction': return [{ op: 'upsertOverviewIntroduction', profileId: s(v.profileId), how: s(v.how), basisClaimIds: strs(v.basisClaimIds), ...(s(v.introLineId) ? { introLineId: s(v.introLineId) } : { introLineId: '' }) }];
    case 'profile': {
      const d = s(v.disposition) as OverviewDisposition;
      return [{
        op: 'upsertOverviewProfile', id: t.id ?? '', sourceName: s(v.sourceName), disposition: d,
        characterId: d === 'KEPT' || d === 'MERGED' ? s(v.characterId) : '',
        personality: strs(v.personality), ...(sameList(strs(v.behavior), strs(initial?.behavior)) ? {} : { behavior: strs(v.behavior) }), setup: strs(v.setup), roleLabel: strs(v.roleLabel), roleTitle: strs(v.roleTitle),
        appearance: { current: strs(v.appearanceCurrent), before: strs(v.appearanceBefore), after: strs(v.appearanceAfter), note: s(v.appearanceNote) },
      }];
    }
    case 'prop': return [{ op: 'upsertOverviewProp', id: t.id ?? '', name: s(v.name), aliases: strs(v.aliases), description: strs(v.description), importance: s(v.importance) }];
    case 'rule': return [{ op: 'upsertOverviewWorldRule', id: t.id ?? '', title: s(v.title), claimIds: strs(v.claimIds) }];
    case 'faction': return [{ op: 'upsertOverviewFaction', id: t.id ?? '', label: s(v.label), kind: (s(v.kind) || 'organization') as 'family' | 'organization', memberProfileIds: strs(v.memberProfileIds), claimIds: strs(v.claimIds) }];
    case 'relationship': return [{ op: 'upsertOverviewRelationship', id: t.id ?? '', aProfileId: s(v.aProfileId), bProfileId: s(v.bProfileId), type: s(v.type) as OverviewRelationshipType, factionId: s(v.factionId), claimIds: strs(v.claimIds) }];
    case 'introLine': return [{ op: 'upsertOverviewIntroLine', id: t.id ?? '', purpose: s(v.purpose) as 'CHARACTER' | 'WORLD_RULE', subjectId: s(v.subjectId), text: s(v.text), basisClaimIds: strs(v.basisClaimIds) }];
    default: return [];
  }
};

// ─── Lựa chọn cho các ô chọn ───────────────────────────────────────────────────────────────────────────────

const clip = (t: string, n: number): string => (t.length > n ? `${t.slice(0, n)}…` : t);

/** Nguồn lựa chọn của một trường (`source`). `purpose` chỉ dùng cho `subjects` (câu V.O.: nhân vật hay luật). */
export const optionsFor = (source: FieldSource, ov: Overview, outline: FilmOutline | null | undefined, ctx: { purpose?: string } = {}): Option[] => {
  switch (source) {
    case 'claims': return ov.claims.map((c) => ({ value: c.id, label: `${c.status === 'ok' ? '' : '⚠ '}${c.id} · ${clip(c.text, 80)}` }));
    case 'profiles': return ov.profiles.map((p) => ({ value: p.id, label: `${p.sourceName} (${p.id})` }));
    case 'cast': return (outline?.characters ?? []).map((c) => ({ value: c.id, label: `${c.name}${c.localizedName && c.localizedName !== c.name ? ` → ${c.localizedName}` : ''}` }));
    case 'factions': return ov.factions.map((f) => ({ value: f.id, label: f.label }));
    case 'rules': return ov.worldRules.map((w) => ({ value: w.id, label: w.title }));
    case 'introLines': return ov.introLines.map((l) => ({ value: l.id, label: `${l.id} · ${clip(l.text, 60)}` }));
    case 'subjects': return ctx.purpose === 'WORLD_RULE' ? optionsFor('rules', ov, outline) : optionsFor('profiles', ov, outline);
    default: return [];
  }
};
